import React, { type ReactNode, useContext, useMemo } from 'react';

import { IntlContext, IntlProvider } from 'react-intl';

import { type LazyMessages } from './types';
import {
	DEFAULT_LOCALE,
	DEFAULT_RICH_TEXT_ELEMENTS,
	getCodesFromLocale,
	useLazyMessages,
} from './utils';

interface Props {
	children: ReactNode;
	locale: string;
	messages: LazyMessages;
}

export const ProformaIntlProvider: React.FC<Props> = ({ children, locale, messages }) => {
	const { language, country } = getCodesFromLocale(locale) ?? {};

	const lazyMessages = useLazyMessages(messages, language ?? DEFAULT_LOCALE, country);

	// Read an ancestor IntlProvider's messages (if any) so that translations
	// owned by the consumer app remain available to content rendered beneath
	// this provider — notably custom renderers passed into proforma surfaces
	// that call <FormattedMessage /> with consumer-owned ids.
	//
	// We read IntlContext directly rather than using useIntl() because useIntl()
	// throws when there is no ancestor provider, and this component is
	// documented to work standalone. Package-owned messages are spread last so
	// they always win on id collisions, preserving the library's canonical
	// translations.
	const parentIntl = useContext(IntlContext);
	const parentMessages = parentIntl?.messages;

	// `IntlProvider` expects a homogeneous Record<string, string> or
	// Record<string, MessageFormatElement[]>. The parent bundle may contain
	// pre-compiled MessageFormatElement[] values, while our lazy bundle is
	// always plain strings; mixing them at the type level is fine at runtime
	// because react-intl handles both per-entry.
	// Hence casting mergedMessages to Record<string, string> is purely done to
	// satisfy Typescript.
	const mergedMessages = useMemo(
		() =>
			(lazyMessages !== undefined
				? { ...(parentMessages ?? {}), ...lazyMessages }
				: undefined) as Record<string, string>,
		[parentMessages, lazyMessages],
	);

	if (mergedMessages === undefined) {
		return null;
	}

	return (
		<IntlProvider
			messages={mergedMessages}
			locale={(country && `${language}-${country}`) || language || locale.replace('_', '-')}
			defaultLocale={DEFAULT_LOCALE}
			defaultRichTextElements={DEFAULT_RICH_TEXT_ELEMENTS}
		>
			{children}
		</IntlProvider>
	);
};
